import React from 'react';
import { injectInternalLinks } from '../lib/utils';

/**
 * Splits HTML content after the second closing </p> tag.
 */
function splitHtmlAfterSecondParagraph(html) {
  if (!html) return { part1: '', part2: '' };
  
  // Find the index of the first heading tag (h1, h2, h3, h4, h5, h6)
  const headingRegex = /<(h[1-6])\b/i;
  const headingMatch = headingRegex.exec(html);
  const headingIndex = headingMatch ? headingMatch.index : -1;
  
  // Find the index of the second closing </p> tag
  const pRegex = /<\/p\s*>/gi;
  let pMatch;
  let pCount = 0;
  let secondPIndex = -1;
  
  while ((pMatch = pRegex.exec(html)) !== null) {
    pCount++;
    if (pCount === 2) {
      secondPIndex = pRegex.lastIndex;
      break;
    }
  }
  
  // If a heading appears before the second paragraph, split before the heading
  if (headingIndex !== -1 && (secondPIndex === -1 || headingIndex < secondPIndex)) {
    return {
      part1: html.substring(0, headingIndex),
      part2: html.substring(headingIndex)
    };
  }
  
  // Otherwise, split after the second paragraph if it exists
  if (secondPIndex !== -1) {
    return {
      part1: html.substring(0, secondPIndex),
      part2: html.substring(secondPIndex)
    };
  }
  
  // Fallback: split after the first paragraph if second doesn't exist
  const firstPRegex = /<\/p\s*>/i;
  const firstPMatch = firstPRegex.exec(html);
  if (firstPMatch) {
    const firstPIndex = firstPRegex.lastIndex;
    return {
      part1: html.substring(0, firstPIndex),
      part2: html.substring(firstPIndex)
    };
  }
  
  return { part1: html, part2: '' };
}

export default function ContentWithCTA({ html, className = "", showCTA = false, currentSlug = "" }) {
  if (!html) return null;

  const linkedHtml = injectInternalLinks(html, currentSlug);

  if (!showCTA) {
    return (
      <div className={className}>
        <div 
          className="prose prose-emerald max-w-none text-[#1C1E1D]/80 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: linkedHtml }} 
        />
      </div>
    );
  }

  const { part1, part2 } = splitHtmlAfterSecondParagraph(linkedHtml);
  const phone = "30 71 33 11";
  const email = "kontakt@logkaps.dk";

  return (
    <div className={className}>
      {/* First 2 paragraphs */}
      <div 
        className="prose prose-emerald max-w-none text-[#1C1E1D]/80 leading-relaxed"
        dangerouslySetInnerHTML={{ __html: part1 }} 
      />

      {/* CTA Box */}
      <div className="my-8 p-6 sm:p-8 bg-[#F4F1EA] border border-[#E2E2E0] rounded-lg flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-sm">
        <div className="space-y-1.5 max-w-xl">
          <h4 className="text-lg font-bold text-[#1C1E1D] font-display">
            Vil du høre mere om, hvordan vi kan hjælpe dig?
          </h4>
          <p className="text-sm text-[#1C1E1D]/70 leading-relaxed">
            Vi står altid klar til at rådgive dig og give et uforpligtende tilbud på dit projekt.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
          <a
            href={`tel:+45${phone.replace(/\s+/g, '')}`}
            className="inline-flex items-center justify-center gap-2.5 rounded-md bg-[#163A2B] px-5 py-3 text-sm font-bold text-white shadow-sm hover:bg-[#1C1E1D] transition-colors cursor-pointer w-full sm:w-auto"
          >
            <i className="fa-solid fa-phone text-sm" />
            <span>Ring {phone}</span>
          </a>
          <a
            href={`mailto:${email}`}
            className="inline-flex items-center justify-center gap-2.5 rounded-md border-2 border-[#163A2B] bg-white px-5 py-3 text-sm font-bold text-[#163A2B] hover:bg-[#163A2B]/5 transition-colors cursor-pointer w-full sm:w-auto"
          >
            <i className="fa-solid fa-envelope text-sm" />
            <span>Send e-mail</span>
          </a>
        </div>
      </div>

      {/* Remaining paragraphs */}
      {part2 && (
        <div 
          className="prose prose-emerald max-w-none text-[#1C1E1D]/80 leading-relaxed mt-6"
          dangerouslySetInnerHTML={{ __html: part2 }} 
        />
      )}
    </div>
  );
}
